/**
 * Global route middleware that restores auth from URL hash fragments.
 *
 * When the Capacitor app opens internal pages in the system browser (Safari),
 * a fresh access_token is appended as a hash fragment (#cc_auth=...).
 * The token refresh happens in the WKWebView before opening Safari,
 * so this middleware always receives a valid, non-expired access_token.
 *
 * This middleware:
 * 1. Extracts the access_token from the route hash
 * 2. Stores the token and fetches the user profile via the store
 * 3. Redirects to the same URL without the hash
 */
export default defineNuxtRouteMiddleware(async to => {
  if (import.meta.server) return

  const hash = to.hash
  if (!hash || !hash.includes('cc_auth=')) return

  try {
    const match = hash.match(/cc_auth=([^&]+)/)
    if (!match) return

    const accessToken = decodeURIComponent(match[1])
    if (!accessToken) return

    const tokenObj = { access_token: accessToken, token_type: 'Bearer' }

    const userStore = useUserStore()
    userStore.setToken(tokenObj)

    try {
      await userStore.profile(null, true)
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[auth-from-hash] profile fetch failed:', err)
    }

    // Redirect to the same URL without the cc_auth hash
    const cleanHash = hash.replace(/[#&]?cc_auth=[^&]*/, '')
    return navigateTo(
      { path: to.path, query: to.query, hash: cleanHash || undefined },
      { replace: true }
    )
  } catch {
    // Auth restoration is best-effort — don't break the page
  }
})
